import React, { useEffect, useState } from 'react';
import { PlusIcon, TrashIcon, LinkIcon, UnlinkIcon, SaveIcon, XIcon, ChevronUpIcon, ChevronDownIcon, SearchIcon, BookIcon } from 'lucide-react';
import { formatCurrency } from '../utils/currencyFormatter';
import { StandardizedNotes, NoteVisibility } from '../common/StandardizedNotes';
import { getDefaultCurrency } from '../../utils/appSettings';
import { GLCodingDialog } from '../../GLCodingDialog';
import { SelectedDimensions } from '../financial/gl-coding/types';
export function CreditNoteEntry({
  onSave,
  locale = 'en-US',
  defaultCurrency
}) {
  const [creditNote, setCreditNote] = useState({
    id: '',
    vendor: '',
    vendorId: '',
    creditNoteNumber: '',
    creditNoteDate: '',
    currency: defaultCurrency || getDefaultCurrency(),
    description: '',
    notes: '',
    noteVisibility: 'Internal' as NoteVisibility,
    noteVisibilityDetails: '',
    linkedInvoices: [],
    lines: [{
      id: 1,
      description: '',
      quantity: 1,
      unitPrice: 0,
      taxCode: 'STD',
      taxRate: 10,
      taxAmount: 0,
      amount: 0,
      glCode: '',
      selectedDimensions: {} as Record<string, string>,
      invoiceRef: '',
      invoiceLineRef: ''
    }]
  });
  const [errors, setErrors] = useState({});
  const [expandedSections, setExpandedSections] = useState({
    header: true,
    vendor: true,
    lines: true,
    linkedInvoices: true
  });
  const [showInvoiceSelector, setShowInvoiceSelector] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [availableInvoices, setAvailableInvoices] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [glCodingLineId, setGlCodingLineId] = useState<number | null>(null);
  // Sample data
  const vendors = [{
    id: 'V001',
    name: 'Office Depot'
  }, {
    id: 'V002',
    name: 'Dell Technologies'
  }, {
    id: 'V003',
    name: 'Server Solutions Inc.'
  }];
  const taxCodes = [{
    code: 'STD',
    name: 'Standard Rate',
    rate: 10
  }, {
    code: 'RED',
    name: 'Reduced Rate',
    rate: 5
  }, {
    code: 'ZERO',
    name: 'Zero Rate',
    rate: 0
  }, {
    code: 'EXM',
    name: 'Exempt',
    rate: 0
  }];
  const glCodes = [{
    code: '1000',
    name: 'Cash and Cash Equivalents'
  }, {
    code: '1100',
    name: 'Accounts Receivable'
  }, {
    code: '2000',
    name: 'Accounts Payable'
  }, {
    code: '2100',
    name: 'Accrued Liabilities'
  }, {
    code: '4000',
    name: 'Revenue'
  }, {
    code: '5000',
    name: 'Cost of Goods Sold'
  }, {
    code: '5100',
    name: 'Direct Materials'
  }, {
    code: '5200',
    name: 'Direct Labour'
  }, {
    code: '6000',
    name: 'Operating Expenses'
  }, {
    code: '6100',
    name: 'Office Supplies'
  }, {
    code: '6200',
    name: 'IT Equipment'
  }, {
    code: '6300',
    name: 'Professional Services'
  }, {
    code: '6400',
    name: 'Travel & Entertainment'
  }, {
    code: '6500',
    name: 'Utilities'
  }, {
    code: '7000',
    name: 'Depreciation & Amortisation'
  }, {
    code: '8000',
    name: 'Other Income'
  }, {
    code: '9000',
    name: 'Tax Expense'
  }];
  const sampleInvoices = [{
    id: 'INV-2023-001',
    number: 'INV-2023-001',
    date: '2023-10-15',
    amount: 1250.75,
    status: 'Approved',
    vendor: 'Office Depot',
    vendorId: 'V001',
    lines: [{
      id: 1,
      description: 'Office Supplies - Paper',
      quantity: 10,
      unitPrice: 25.5,
      amount: 255.0
    }, {
      id: 2,
      description: 'Office Supplies - Pens',
      quantity: 50,
      unitPrice: 1.25,
      amount: 62.5
    }]
  }, {
    id: 'INV-2023-002',
    number: 'INV-2023-002',
    date: '2023-10-18',
    amount: 4300.0,
    status: 'Approved',
    vendor: 'Dell Technologies',
    vendorId: 'V002',
    lines: [{
      id: 1,
      description: 'Laptop - XPS 15',
      quantity: 2,
      unitPrice: 1800.0,
      amount: 3600.0
    }, {
      id: 2,
      description: 'Monitor - 27"',
      quantity: 2,
      unitPrice: 350.0,
      amount: 700.0
    }]
  }, {
    id: 'INV-2023-003',
    number: 'INV-2023-003',
    date: '2023-09-15',
    amount: 7500.0,
    status: 'Approved',
    vendor: 'Dell Technologies',
    vendorId: 'V002',
    lines: [{
      id: 1,
      description: 'Server - PowerEdge R740',
      quantity: 1,
      unitPrice: 6000.0,
      amount: 6000.0
    }, {
      id: 2,
      description: 'Server RAM Upgrade',
      quantity: 4,
      unitPrice: 375.0,
      amount: 1500.0
    }]
  }, {
    id: 'INV-2023-004',
    number: 'INV-2023-004',
    date: '2023-09-28',
    amount: 12350.0,
    status: 'Approved',
    vendor: 'Server Solutions Inc.',
    vendorId: 'V003',
    lines: [{
      id: 1,
      description: 'Network Infrastructure Setup',
      quantity: 1,
      unitPrice: 8500.0,
      amount: 8500.0
    }, {
      id: 2,
      description: 'Managed Services - 3 months',
      quantity: 3,
      unitPrice: 1250.0,
      amount: 3750.0
    }, {
      id: 3,
      description: 'Hardware Installation',
      quantity: 1,
      unitPrice: 100.0,
      amount: 100.0
    }]
  }];
  // Function to perform the search
  const performSearch = () => {
    setIsSearching(true);
    setTimeout(() => {
      const searchTermLower = searchTerm.toLowerCase().trim();
      if (!searchTermLower) {
        setFilteredInvoices(availableInvoices);
        setIsSearching(false);
        return;
      }
      const filtered = availableInvoices.filter(invoice => invoice.number.toLowerCase().includes(searchTermLower) || invoice.vendor.toLowerCase().includes(searchTermLower) || invoice.id && invoice.id.toLowerCase().includes(searchTermLower));
      setFilteredInvoices(filtered);
      setIsSearching(false);
    }, 300);
  };
  // Update filtered invoices when the modal opens
  useEffect(() => {
    if (showInvoiceSelector) {
      performSearch();
    }
  }, [showInvoiceSelector]);
  // Toggle section expansion
  const toggleSection = section => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section]
    });
  };
  // Handle form field changes
  const handleInputChange = e => {
    const {
      name,
      value
    } = e.target;
    setCreditNote({
      ...creditNote,
      [name]: value
    });
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  // Add a new line item
  const addLineItem = () => {
    const newId = creditNote.lines.length > 0 ? Math.max(...creditNote.lines.map(item => item.id)) + 1 : 1;
    const newLine = {
      id: newId,
      description: '',
      quantity: 1,
      unitPrice: 0,
      taxCode: 'STD',
      taxRate: 10,
      taxAmount: 0,
      amount: 0,
      glCode: '',
      selectedDimensions: {} as Record<string, string>,
      invoiceRef: '',
      invoiceLineRef: ''
    };
    setCreditNote(prevState => ({
      ...prevState,
      lines: [...prevState.lines, newLine]
    }));
  };
  // Remove a line item
  const removeLineItem = id => {
    const updatedLines = creditNote.lines.filter(line => line.id !== id);
    setCreditNote(prevState => ({
      ...prevState,
      lines: updatedLines,
      amount: updatedLines.reduce((sum, line) => sum + (line.amount || 0), 0)
    }));
  };
  // GL Coding Dialog handler
  const handleGLCodeApply = (lineId: number, glCode: string, dimensions: SelectedDimensions) => {
    const updatedLines = creditNote.lines.map(line => {
      if (line.id === lineId) {
        return {
          ...line,
          glCode,
          selectedDimensions: dimensions
        };
      }
      return line;
    });
    setCreditNote(prev => ({
      ...prev,
      lines: updatedLines
    }));
    setGlCodingLineId(null);
  };
  // Get the line item currently being GL coded
  const glCodingLine = glCodingLineId !== null ? creditNote.lines.find(line => line.id === glCodingLineId) : null;
  // Handle line item changes
  const handleLineItemChange = (index, field, value) => {
    const updatedLines = [...creditNote.lines];
    if (field === 'taxCode') {
      const selectedTaxCode = taxCodes.find(tax => tax.code === value);
      const taxRate = selectedTaxCode ? selectedTaxCode.rate : 0;
      updatedLines[index] = {
        ...updatedLines[index],
        [field]: value,
        taxRate
      };
      const lineValue = updatedLines[index].quantity * updatedLines[index].unitPrice;
      updatedLines[index].taxAmount = calculateTaxAmount(lineValue, taxCodes.find(tax => tax.code === value));
      updatedLines[index].amount = lineValue + updatedLines[index].taxAmount;
    } else if (field === 'quantity' || field === 'unitPrice') {
      updatedLines[index] = {
        ...updatedLines[index],
        [field]: typeof value === 'number' ? value : parseFloat(value) || 0
      };
      const lineValue = updatedLines[index].quantity * updatedLines[index].unitPrice;
      updatedLines[index].taxAmount = calculateTaxAmount(lineValue, taxCodes.find(tax => tax.code === updatedLines[index].taxCode));
      updatedLines[index].amount = lineValue + updatedLines[index].taxAmount;
    } else {
      updatedLines[index] = {
        ...updatedLines[index],
        [field]: value
      };
    }
    setCreditNote({
      ...creditNote,
      lines: updatedLines,
      amount: updatedLines.reduce((sum, line) => sum + (line.amount || 0), 0)
    });
  };
  // Open invoice selector to link an invoice
  const openInvoiceSelector = () => {
    setSearchTerm('');
    const vendorInvoices = sampleInvoices.filter(inv => inv.vendorId === creditNote.vendorId).filter(inv => !creditNote.linkedInvoices.some(linked => linked.id === inv.id));
    setAvailableInvoices(vendorInvoices);
    setFilteredInvoices(vendorInvoices);
    setShowInvoiceSelector(true);
  };
  // Link an invoice to the credit note
  const linkInvoice = invoice => {
    if (creditNote.linkedInvoices.some(inv => inv.id === invoice.id)) {
      return;
    }
    setCreditNote(prevState => ({
      ...prevState,
      linkedInvoices: [...prevState.linkedInvoices, invoice]
    }));
    setShowInvoiceSelector(false);
  };
  // Remove a linked invoice
  const removeLinkedInvoice = invoiceId => {
    setCreditNote(prevState => ({
      ...prevState,
      linkedInvoices: prevState.linkedInvoices.filter(inv => inv.id !== invoiceId)
    }));
  };
  // Calculate tax amount based on value and tax code
  const calculateTaxAmount = (value, taxCode) => {
    if (!taxCode) return 0;
    const taxRate = typeof taxCode === 'object' ? taxCode.rate : taxCodes.find(tax => tax.code === taxCode)?.rate || 0;
    return value * taxRate / 100;
  };
  // Validate form before submission
  const validateForm = () => {
    const newErrors = {};
    if (!creditNote.vendor) newErrors.vendor = 'Vendor is required';
    if (!creditNote.creditNoteNumber) newErrors.creditNoteNumber = 'Credit note number is required';
    if (!creditNote.creditNoteDate) newErrors.creditNoteDate = 'Credit note date is required';
    if (!creditNote.currency) newErrors.currency = 'Currency is required';
    const lineErrors = [];
    creditNote.lines.forEach((line, index) => {
      const lineError = {};
      if (!line.description) lineError.description = 'Description is required';
      if (line.quantity <= 0) lineError.quantity = 'Quantity must be greater than 0';
      if (Object.keys(lineError).length > 0) {
        lineErrors[index] = lineError;
      }
    });
    if (lineErrors.length > 0) {
      newErrors.lines = lineErrors;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  // Handle form submission
  const handleSubmit = (asDraft = true) => {
    if (!asDraft && !validateForm()) {
      return;
    }
    onSave(creditNote, asDraft);
  };
  // Handle search input keydown event
  const handleSearchKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      performSearch();
    }
  };
  return <div className="mb-8" data-id="element-3418">
      <div className="flex justify-between items-center mb-6" data-id="element-3419">
        <div data-id="element-3420">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-3421">
            Credit Note Entry
          </h2>
          <p className="mt-1 text-sm text-gray-500" data-id="element-3422">
            Create a new credit note, optionally linking to existing invoices
          </p>
        </div>
      </div>

      {/* Credit Note Header Information */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6" data-id="element-3423">
        <div className="px-4 py-4 border-b border-gray-200 flex justify-between items-center cursor-pointer" onClick={() => toggleSection('header')} data-id="element-3424">
          <h4 className="text-sm font-medium text-gray-900" data-id="element-3425">
            Credit Note Header Information
          </h4>
          {expandedSections.header ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-3426" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-3427" />}
        </div>
        {expandedSections.header && <div className="px-4 py-5 sm:p-6" data-id="element-3428">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2" data-id="element-3429">
              <div className="sm:col-span-2" data-id="element-3430">
                <label htmlFor="creditNoteNumber" className="block text-sm font-medium text-gray-700" data-id="element-3431">
                  Credit Note Number *
                </label>
                <div className="mt-1" data-id="element-3432">
                  <input type="text" name="creditNoteNumber" id="creditNoteNumber" value={creditNote.creditNoteNumber} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.creditNoteNumber ? 'border-red-300' : ''}`} placeholder="Enter credit note number" data-id="element-3433" />
                  {errors.creditNoteNumber && <p className="mt-1 text-sm text-red-600" data-id="element-3434">
                      {errors.creditNoteNumber}
                    </p>}
                </div>
              </div>
              <div data-id="element-3435">
                <label htmlFor="creditNoteDate" className="block text-sm font-medium text-gray-700" data-id="element-3436">
                  Credit Note Date *
                </label>
                <div className="mt-1" data-id="element-3437">
                  <input type="date" name="creditNoteDate" id="creditNoteDate" value={creditNote.creditNoteDate} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.creditNoteDate ? 'border-red-300' : ''}`} data-id="element-3438" />
                  {errors.creditNoteDate && <p className="mt-1 text-sm text-red-600" data-id="element-3439">
                      {errors.creditNoteDate}
                    </p>}
                </div>
              </div>
              <div data-id="element-3440">
                <label htmlFor="currency" className="block text-sm font-medium text-gray-700" data-id="element-3441">
                  Currency *
                </label>
                <div className="mt-1" data-id="element-3442">
                  <select id="currency" name="currency" value={creditNote.currency} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.currency ? 'border-red-300' : ''}`} data-id="element-3443">
                    <option value="USD" data-id="element-3444">USD - US Dollar</option>
                    <option value="EUR" data-id="element-3445">EUR - Euro</option>
                    <option value="GBP" data-id="element-3446">GBP - British Pound</option>
                    <option value="AUD" data-id="element-3447">AUD - Australian Dollar</option>
                    <option value="CAD" data-id="element-3448">CAD - Canadian Dollar</option>
                  </select>
                  {errors.currency && <p className="mt-1 text-sm text-red-600" data-id="element-3449">
                      {errors.currency}
                    </p>}
                </div>
              </div>
            </div>
          </div>}
      </div>

      {/* Vendor Information */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6" data-id="element-3450">
        <div className="px-4 py-4 border-b border-gray-200 flex justify-between items-center cursor-pointer" onClick={() => toggleSection('vendor')} data-id="element-3451">
          <h4 className="text-sm font-medium text-gray-900" data-id="element-3452">
            Vendor Information
          </h4>
          {expandedSections.vendor ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-3453" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-3454" />}
        </div>
        {expandedSections.vendor && <div className="px-4 py-5 sm:p-6" data-id="element-3455">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2" data-id="element-3456">
              <div className="sm:col-span-2" data-id="element-3457">
                <label htmlFor="vendor" className="block text-sm font-medium text-gray-700" data-id="element-3458">
                  Vendor *
                </label>
                <div className="mt-1" data-id="element-3459">
                  <select id="vendor" name="vendor" value={creditNote.vendor} onChange={e => {
                const selectedVendor = vendors.find(v => v.name === e.target.value);
                setCreditNote({
                  ...creditNote,
                  vendor: e.target.value,
                  vendorId: selectedVendor ? selectedVendor.id : ''
                });
              }} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.vendor ? 'border-red-300' : ''}`} data-id="element-3460">
                    <option value="" data-id="element-3461">Select vendor</option>
                    {vendors.map(vendor => <option key={vendor.id} value={vendor.name} data-id="element-3462">
                        {vendor.name}
                      </option>)}
                  </select>
                  {errors.vendor && <p className="mt-1 text-sm text-red-600" data-id="element-3463">{errors.vendor}</p>}
                </div>
              </div>
              <div className="sm:col-span-2" data-id="element-3464">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-3465">
                  Description
                </label>
                <div className="mt-1" data-id="element-3466">
                  <textarea id="description" name="description" rows={3} value={creditNote.description} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" placeholder="Enter credit note description" data-id="element-3467" />
                </div>
              </div>
            </div>
          </div>}
      </div>

      {/* Linked Invoices */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6" data-id="element-3468">
        <div className="px-4 py-4 border-b border-gray-200 flex justify-between items-center cursor-pointer" onClick={() => toggleSection('linkedInvoices')} data-id="element-3469">
          <h4 className="text-sm font-medium text-gray-900" data-id="element-3470">Linked Invoices</h4>
          {expandedSections.linkedInvoices ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-3471" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-3472" />}
        </div>
        {expandedSections.linkedInvoices && <div className="px-4 py-5 sm:p-6" data-id="element-3473">
            <div className="mb-4 flex justify-end" data-id="element-3474">
              <button type="button" onClick={openInvoiceSelector} disabled={!creditNote.vendorId} className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${!creditNote.vendorId ? 'bg-indigo-300 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`} data-id="element-3475">
                <LinkIcon className="h-4 w-4 mr-1" data-id="element-3476" />
                Link Invoice
              </button>
            </div>
            {!creditNote.vendorId && <div className="text-center py-2 mb-4 text-sm text-amber-600 bg-amber-50 rounded-md" data-id="element-3477">
                Please select a vendor first to link invoices
              </div>}
            {creditNote.linkedInvoices.length > 0 ? <div className="overflow-x-auto" data-id="element-3478">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-3479">
                  <thead className="bg-gray-50" data-id="element-3480">
                    <tr data-id="element-3481">
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3482">
                        Invoice Number
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3483">
                        Date
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3484">
                        Amount
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3485">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-3486">
                    {creditNote.linkedInvoices.map(invoice => <tr key={invoice.id} data-id="element-3487">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-3488">
                          {invoice.number}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-3489">
                          {invoice.date}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-3490">
                          {formatCurrency(invoice.amount, creditNote.currency, locale)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-3491">
                          <button onClick={() => removeLinkedInvoice(invoice.id)} className="text-red-600 hover:text-red-900" title="Unlink Invoice" data-id="element-3492">
                            <UnlinkIcon className="h-5 w-5" data-id="element-3493" />
                          </button>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div> : <div className="text-center py-4 text-sm text-gray-500" data-id="element-3494">
                No invoices linked. Click "Link Invoice" to associate this
                credit note with an invoice.
              </div>}
          </div>}
      </div>

      {/* Line Items */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6" data-id="element-3495">
        <div className="px-4 py-4 border-b border-gray-200 flex justify-between items-center cursor-pointer" onClick={() => toggleSection('lines')} data-id="element-3496">
          <h4 className="text-sm font-medium text-gray-900" data-id="element-3497">Line Items</h4>
          {expandedSections.lines ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-3498" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-3499" />}
        </div>
        {expandedSections.lines && <div className="px-4 py-5 sm:p-6" data-id="element-3500">
            <div className="mb-4 flex justify-end" data-id="element-3501">
              <button type="button" onClick={addLineItem} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-3502">
                <PlusIcon className="h-4 w-4 mr-1" data-id="element-3503" />
                Add Line Item
              </button>
            </div>
            <div className="overflow-x-auto" data-id="element-3504">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-3505">
                <thead className="bg-gray-50" data-id="element-3506">
                  <tr data-id="element-3507">
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3508">
                      Description
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3509">
                      Qty
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3510">
                      Unit Price
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3511">
                      Tax Code
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3512">
                      GL Code
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3513">
                      Tax Amount
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3514">
                      Total
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3515">
                      Invoice Ref
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3516">
                      Invoice Line Ref
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3517">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-3518">
                  {creditNote.lines.map((line, index) => <tr key={line.id} data-id="element-3519">
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-3520">
                        <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={line.description} onChange={e => handleLineItemChange(index, 'description', e.target.value)} placeholder="Description" data-id="element-3521" />
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-3522">
                        <input type="number" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-20 sm:text-sm border-gray-300 rounded-md" value={line.quantity} onChange={e => handleLineItemChange(index, 'quantity', parseFloat(e.target.value) || 0)} min="0" step="1" data-id="element-3523" />
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-3524">
                        <div className="relative rounded-md shadow-sm" data-id="element-3525">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3526">
                            <span className="text-gray-500 sm:text-sm" data-id="element-3527">
                              {creditNote.currency === 'USD' ? '$' : creditNote.currency}
                            </span>
                          </div>
                          <input type="text" value={line.unitPrice} onChange={e => {
                      const sanitizedValue = e.target.value.replace(/[^\d.]/g, '');
                      handleLineItemChange(index, 'unitPrice', parseFloat(sanitizedValue) || 0);
                    }} className="block w-28 pl-7 pr-3 shadow-sm sm:text-sm border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-right" placeholder="0.00" data-id="element-3528" />
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-3529">
                        <select className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" value={line.taxCode} onChange={e => handleLineItemChange(index, 'taxCode', e.target.value)} data-id="element-3530">
                          {taxCodes.map(code => <option key={code.code} value={code.code} data-id="element-3531">
                              {code.code} - {code.name}
                            </option>)}
                        </select>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-3532">
                        <button type="button" onClick={() => setGlCodingLineId(line.id)} className={`inline-flex items-center px-3 py-1.5 border rounded-md shadow-sm text-sm leading-4 font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${line.glCode ? 'border-green-300 text-green-800 bg-green-50 hover:bg-green-100' : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'}`} data-id="element-3533">
                          <BookIcon className="h-3.5 w-3.5 mr-1.5" data-id="element-3534" />
                          {line.glCode || 'Set GL Code'}
                        </button>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-3535">
                        {formatCurrency(line.taxAmount, creditNote.currency, locale)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-3536">
                        {formatCurrency(line.amount, creditNote.currency, locale)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-3537">
                        <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-32 sm:text-sm border-gray-300 rounded-md" value={line.invoiceRef || ''} onChange={e => handleLineItemChange(index, 'invoiceRef', e.target.value)} placeholder="e.g. INV-001" data-id="element-3538" />
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-3539">
                        <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-28 sm:text-sm border-gray-300 rounded-md" value={line.invoiceLineRef || ''} onChange={e => handleLineItemChange(index, 'invoiceLineRef', e.target.value)} placeholder="e.g. Line 1" data-id="element-3540" />
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-3541">
                        <button onClick={() => removeLineItem(line.id)} className="text-red-600 hover:text-red-900" title="Remove Line" data-id="element-3542">
                          <TrashIcon className="h-5 w-5" data-id="element-3543" />
                        </button>
                      </td>
                    </tr>)}
                </tbody>
                <tfoot className="bg-gray-50" data-id="element-3544">
                  <tr data-id="element-3545">
                    <td colSpan={6} className="px-4 py-4 text-right text-sm font-medium text-gray-900" data-id="element-3546">
                      Total Amount:
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-bold text-gray-900" data-id="element-3547">
                      {formatCurrency(creditNote.amount, creditNote.currency, locale)}
                    </td>
                    <td colSpan={3} data-id="element-3548"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>}
      </div>

      {/* Notes Section */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6" data-id="element-3549">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-3550">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-3551">Notes</h3>
        </div>
        <div className="px-4 py-5 sm:p-6" data-id="element-3552">
          <StandardizedNotes value={creditNote.notes} onChange={value => setCreditNote({
          ...creditNote,
          notes: value
        })} visibility={creditNote.noteVisibility} onVisibilityChange={visibility => setCreditNote({
          ...creditNote,
          noteVisibility: visibility
        })} visibilityDetails={creditNote.noteVisibilityDetails} onVisibilityDetailsChange={details => setCreditNote({
          ...creditNote,
          noteVisibilityDetails: details
        })} label="Credit Note Notes" placeholder="Add any notes about this credit note. Select who can view these notes using the visibility options." data-id="element-3553" />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="mt-6" data-id="element-3554">
        <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-3555">
          <div className="px-4 py-3 bg-gray-50 sm:px-6 flex justify-end space-x-3" data-id="element-3556">
            <button type="button" onClick={() => handleSubmit(true)} className="inline-flex items-center justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-3557">
              <SaveIcon className="h-4 w-4 mr-1" data-id="element-3558" />
              Save as Draft
            </button>
            <button type="button" onClick={() => handleSubmit(false)} className="inline-flex items-center justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-3559">
              <SaveIcon className="h-4 w-4 mr-1" data-id="element-3560" />
              Submit Credit Note
            </button>
          </div>
        </div>
      </div>

      {/* GL Coding Dialog */}
      {glCodingLine && <GLCodingDialog lineItem={{
      id: glCodingLine.id,
      description: glCodingLine.description || `Line ${glCodingLine.id}`,
      glCode: glCodingLine.glCode,
      selectedDimensions: glCodingLine.selectedDimensions || undefined,
      vendor: creditNote.vendorId ? {
        id: creditNote.vendorId,
        name: creditNote.vendor
      } : undefined,
      dimensionCount: 5
    }} onApply={(glCode, dimensions) => handleGLCodeApply(glCodingLine.id, glCode, dimensions)} onClose={() => setGlCodingLineId(null)} data-id="element-3561" />}

      {/* Invoice Selector Modal */}
      {showInvoiceSelector && <div className="fixed inset-0 overflow-y-auto z-50" data-id="element-3562">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-3563">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true" data-id="element-3564">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-3565"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-3566">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-3567">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-3568">
                <div className="sm:flex sm:items-start" data-id="element-3569">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full" data-id="element-3570">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title" data-id="element-3571">
                      Select Invoice to Link
                    </h3>
                    <p className="mt-1 text-sm text-gray-500" data-id="element-3572">
                      Link invoices for reference. This does not create a
                      foreign key relationship.
                    </p>
                    <div className="mt-3" data-id="element-3573">
                      <div className="mb-4" data-id="element-3574">
                        <div className="flex items-center" data-id="element-3575">
                          <div className="relative flex-grow" data-id="element-3576">
                            <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border pr-10" placeholder="Search invoices..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} onKeyDown={handleSearchKeyDown} data-id="element-3577" />
                            {searchTerm && <button className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => {
                          setSearchTerm('');
                          performSearch();
                        }} data-id="element-3578">
                                <XIcon className="h-4 w-4 text-gray-400" data-id="element-3579" />
                              </button>}
                          </div>
                          <button className="ml-2 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" onClick={performSearch} data-id="element-3580">
                            <SearchIcon className="h-4 w-4 text-gray-500" data-id="element-3581" />
                            <span className="ml-1" data-id="element-3582">Search</span>
                          </button>
                        </div>
                      </div>
                      <div className="max-h-60 overflow-y-auto" data-id="element-3583">
                        <table className="min-w-full divide-y divide-gray-200" data-id="element-3584">
                          <thead className="bg-gray-50" data-id="element-3585">
                            <tr data-id="element-3586">
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3587">
                                Invoice #
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3588">
                                Date
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3589">
                                Amount
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3590">
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200" data-id="element-3591">
                            {isSearching ? <tr data-id="element-3592">
                                <td colSpan={4} className="text-center py-4" data-id="element-3593">
                                  <div className="flex justify-center items-center" data-id="element-3594">
                                    <svg className="animate-spin h-5 w-5 text-indigo-500 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-3595">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-3596"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-3597"></path>
                                    </svg>
                                    <span data-id="element-3598">Searching...</span>
                                  </div>
                                </td>
                              </tr> : filteredInvoices.length > 0 ? filteredInvoices.map(invoice => <tr key={invoice.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => linkInvoice(invoice)} data-id="element-3599">
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-3600">
                                    {invoice.number}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-3601">
                                    {invoice.date}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-3602">
                                    {formatCurrency(invoice.amount, creditNote.currency, locale)}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-3603">
                                    <button onClick={e => {
                              e.stopPropagation();
                              linkInvoice(invoice);
                            }} className="text-indigo-600 hover:text-indigo-900" data-id="element-3604">
                                      Link
                                    </button>
                                  </td>
                                </tr>) : <tr data-id="element-3605">
                                <td colSpan={4} className="text-center py-4 text-sm text-gray-500" data-id="element-3606">
                                  {creditNote.vendorId ? searchTerm ? 'No invoices found matching your search.' : 'No available invoices for this vendor.' : 'Please select a vendor first to see available invoices.'}
                                </td>
                              </tr>}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-3607">
                <button type="button" onClick={() => {
              setShowInvoiceSelector(false);
              setSearchTerm('');
            }} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-3608">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}